import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Checkbox,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Snackbar,
  Tooltip,
  Chip,
  TextField,
  Grid,
} from '@mui/material';
import { NavigateNext, Save, Info } from '@mui/icons-material';
import {
  useGetAvailabilityConfigQuery,
  useUpdateAvailabilityConfigMutation,
} from '../../slices/availabilityConfigApiSlice';

// Kept in sync with SHIFT_PRESETS on the staff availability screen
// (screens/StaffAvailabilityViewStaff.jsx) and with the backend
// availabilityConfigController.js.
const PRESET_COLUMNS = [
  { key: 'LD', label: 'Long Day', hint: '8:00 - 20:00' },
  { key: 'ND', label: 'Night Duty', hint: '20:00 - 8:00' },
  { key: 'AM', label: 'Morning', hint: '8:00 - 16:00' },
  { key: 'PM', label: 'Afternoon', hint: '14:00 - 20:00' },
  { key: 'TW', label: 'Twilight', hint: '20:00 - 00:00' },
  { key: 'ELD', label: 'Extended Long Day', hint: '7:30 - 20:30' },
  { key: 'ESCORT', label: 'Escort', hint: '8:00 - 20:00' },
];

const DAY_ROWS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const defaultDayState = () => ({ anyTimesAllowed: true, allowedPresets: [] });

// Kept in sync with the schema defaults in backend/models/availabilityConfigModel.js.
const defaultSpecialsWindowState = () => ({ staff: 48, staffFriday: 72, admin: 720, adminFriday: 720 });

const SPECIALS_WINDOW_FIELDS = [
  { key: 'staff', label: 'Staff / Manager requests', hint: 'Applies to pre-shift requests submitted by staff or managers (non-Friday).' },
  { key: 'staffFriday', label: 'Staff / Manager requests — Friday', hint: 'Overrides the value above when the request is submitted on a Friday.' },
  { key: 'admin', label: 'Admin requests', hint: 'Applies to shifts created or updated directly by an admin (non-Friday).' },
  { key: 'adminFriday', label: 'Admin requests — Friday', hint: 'Overrides the value above when the request is submitted on a Friday.' },
];

const AvailabilityConfigScreen = () => {
  const navigate = useNavigate();

  const { data: configData, isLoading, isFetching, error, refetch } = useGetAvailabilityConfigQuery();
  const [updateAvailabilityConfig, { isLoading: saving }] = useUpdateAvailabilityConfigMutation();

  // Local editable copy of the config, keyed by day.
  const [dayState, setDayState] = useState(() => {
    const initial = {};
    DAY_ROWS.forEach((d) => { initial[d.key] = defaultDayState(); });
    return initial;
  });
  // Local editable copy of the specials/cohorts booking-window hours.
  const [specialsWindowState, setSpecialsWindowState] = useState(defaultSpecialsWindowState);
  const [isDirty, setIsDirty] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load server data into local state whenever it (re)loads, as long as
  // there are no unsaved local edits.
  useEffect(() => {
    if (configData && !isDirty) {
      const loaded = {};
      DAY_ROWS.forEach((d) => {
        const dayConfig = configData[d.key];
        loaded[d.key] = {
          anyTimesAllowed: dayConfig?.anyTimesAllowed !== false,
          allowedPresets: Array.isArray(dayConfig?.allowedPresets) ? dayConfig.allowedPresets : [],
        };
      });
      setDayState(loaded);

      const sw = configData.specialsWindow || {};
      const defaults = defaultSpecialsWindowState();
      setSpecialsWindowState({
        staff: Number.isFinite(sw.staff) ? sw.staff : defaults.staff,
        staffFriday: Number.isFinite(sw.staffFriday) ? sw.staffFriday : defaults.staffFriday,
        admin: Number.isFinite(sw.admin) ? sw.admin : defaults.admin,
        adminFriday: Number.isFinite(sw.adminFriday) ? sw.adminFriday : defaults.adminFriday,
      });
    }
  }, [configData, isDirty]);

  const handleToggleAnyTimes = (dayKey) => {
    setDayState((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        anyTimesAllowed: !prev[dayKey].anyTimesAllowed,
      },
    }));
    setIsDirty(true);
  };

  const handleTogglePreset = (dayKey, presetKey) => {
    setDayState((prev) => {
      const current = prev[dayKey];
      const isSelected = current.allowedPresets.includes(presetKey);
      const allowedPresets = isSelected
        ? current.allowedPresets.filter((p) => p !== presetKey)
        : [...current.allowedPresets, presetKey];
      return {
        ...prev,
        [dayKey]: { ...current, allowedPresets },
      };
    });
    setIsDirty(true);
  };

  const handleSpecialsWindowChange = (fieldKey, rawValue) => {
    // Allow the field to be cleared while typing without immediately
    // snapping back to 0 — Save still only ever sends a sane number because
    // the backend re-validates/falls back server-side too.
    const value = rawValue === '' ? '' : Math.max(0, Number(rawValue) || 0);
    setSpecialsWindowState((prev) => ({ ...prev, [fieldKey]: value }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    try {
      const defaults = defaultSpecialsWindowState();
      const sanitizedSpecialsWindow = {
        staff: specialsWindowState.staff === '' ? defaults.staff : specialsWindowState.staff,
        staffFriday: specialsWindowState.staffFriday === '' ? defaults.staffFriday : specialsWindowState.staffFriday,
        admin: specialsWindowState.admin === '' ? defaults.admin : specialsWindowState.admin,
        adminFriday: specialsWindowState.adminFriday === '' ? defaults.adminFriday : specialsWindowState.adminFriday,
      };
      await updateAvailabilityConfig({ ...dayState, specialsWindow: sanitizedSpecialsWindow }).unwrap();
      setIsDirty(false);
      setSnackbar({ open: true, message: 'Availability configuration saved successfully', severity: 'success' });
      refetch();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.data?.message || 'Failed to save availability configuration',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const summaryForDay = useMemo(() => (dayKey) => {
    const day = dayState[dayKey];
    if (!day) return '';
    if (day.anyTimesAllowed) return 'Any times allowed';
    if (day.allowedPresets.length === 0) return 'No shift types configured — staff cannot log availability';
    return day.allowedPresets
      .map((key) => PRESET_COLUMNS.find((p) => p.key === key)?.label || key)
      .join(', ');
  }, [dayState]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Navigation Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
        >
          Dashboard
        </Link>
        <Typography color="text.primary">Availability Type Configuration</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Availability Type Configuration</Typography>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={saving || !isDirty}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load the current configuration. {error?.data?.message || ''}
        </Alert>
      )}

      <Alert severity="info" icon={<Info />} sx={{ mb: 3 }}>
        For each day of the week, choose whether staff can enter <strong>any</strong> start/end time when logging
        availability, or restrict them to one or more fixed shift times. When a fixed shift time is selected for a
        day, staff will only be able to pick from those shift times for that day — they won't be able to type a
        custom start/end time. This does <strong>not</strong> affect unavailability (annual leave, sick leave, etc.)
        — staff can still mark themselves unavailable with any dates/times as before.
      </Alert>

      <TableContainer component={Paper} elevation={3}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Day</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                Any Times Allowed
              </TableCell>
              {PRESET_COLUMNS.map((preset) => (
                <TableCell key={preset.key} align="center" sx={{ fontWeight: 'bold' }}>
                  <Tooltip title={preset.hint}>
                    <span>
                      {preset.label}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {preset.hint}
                      </Typography>
                    </span>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {DAY_ROWS.map((day) => {
              const state = dayState[day.key] || defaultDayState();
              return (
                <TableRow key={day.key} hover>
                  <TableCell sx={{ fontWeight: 500 }}>
                    {day.label}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {summaryForDay(day.key)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={state.anyTimesAllowed}
                      onChange={() => handleToggleAnyTimes(day.key)}
                      color="primary"
                    />
                  </TableCell>
                  {PRESET_COLUMNS.map((preset) => (
                    <TableCell key={preset.key} align="center">
                      <Checkbox
                        checked={state.allowedPresets.includes(preset.key)}
                        onChange={() => handleTogglePreset(day.key, preset.key)}
                        disabled={state.anyTimesAllowed}
                        color="secondary"
                      />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Paper elevation={3} sx={{ mt: 3, p: 2 }}>
        <Typography variant="h6" gutterBottom>Specials / Cohorts Booking Window</Typography>
        <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
          Controls how far in advance a <strong>1-1 Special</strong> or <strong>Cohorts</strong> request can be
          submitted for. Staff/manager requests go through the pre-shift approval flow; admin requests are created
          or updated directly on a shift. Each group has its own value, plus a separate override applied when the
          request is submitted on a Friday.
        </Alert>
        <Grid container spacing={2}>
          {SPECIALS_WINDOW_FIELDS.map((field) => (
            <Grid item xs={12} sm={6} md={3} key={field.key}>
              <Tooltip title={field.hint}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={field.label}
                  value={specialsWindowState[field.key]}
                  onChange={(e) => handleSpecialsWindowChange(field.key, e.target.value)}
                  InputProps={{ inputProps: { min: 0 }, endAdornment: <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>hours</Typography> }}
                />
              </Tooltip>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {isDirty && (
          <Chip label="You have unsaved changes" color="warning" size="small" />
        )}
        {isFetching && !isLoading && (
          <Chip label="Refreshing..." size="small" variant="outlined" />
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default AvailabilityConfigScreen;